import axios from 'axios';
import Cookies from 'js-cookie';

// API base URL - always use direct URL
const BASE_URL = 'https://api.tazatropics.com/public/api/';

// Helper to convert object to URLSearchParams - matching Pure Meat's JSON_to_URLEncoded
const toFormData = (element, key = null, list = []) => {
  if (typeof element === 'object' && element !== null && !(element instanceof Date)) {
    for (let idx in element) {
      toFormData(
        element[idx],
        key ? `${key}[${idx}]` : idx,
        list
      );
    }
  } else {
    list.push(`${key}=${encodeURIComponent(element)}`);
  }
  return list.join('&');
};

// Get user's current location coordinates
export const getCurrentLocation = () => {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('Geolocation is not supported by this browser'));
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        resolve({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude
        });
      },
      (error) => {
        reject(error);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 300000 // 5 minutes
      }
    );
  });
};

// Get all stores for chat support using geolocation
export const getAllStoresForChat = async () => {
  const defaultStore = {
    id: 2, // Known store user ID
    name: 'Store Support',
    address: 'Main Store Location',
    phone: '+1 248 497 9083'
  };

  try {
    // Use fixed coordinates directly without asking for location permission
    const userCoords = {
      latitude: 42.457559,   // Fixed coordinates from home page
      longitude: -83.205308
    };

    // Search stores by coordinates
    try {
      const locationBody = toFormData({
        latitude: userCoords.latitude,
        longitude: userCoords.longitude,
        radius: 50 // 50km radius
      });

      const response = await axios.post(`${BASE_URL}v1/home/searchStoreByLocation`, locationBody, {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded'
        },
        timeout: 10000
      });

      if (response.data && response.data.status === 200 && response.data.data && response.data.data.length > 0) {
        return { success: true, data: response.data.data };
      }
    } catch (locationSearchError) {
      // Ignored: fallback to default city search
    }

    // Fallback: Try to get stores from default city
    try {
      const cityBody = toFormData({ id: 1 });
      const cityResponse = await axios.post(`${BASE_URL}v1/home/searchStoreWithCity`, cityBody, {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded'
        },
        timeout: 10000
      });

      if (cityResponse.data && cityResponse.data.status === 200 && cityResponse.data.data && cityResponse.data.data.length > 0) {
        return { success: true, data: cityResponse.data.data };
      }
    } catch (cityError) {
      // Ignored: fallback to last resort
    }

    // Last resort: return a known store
    return {
      success: true,
      data: [defaultStore]
    };

  } catch (error) {
    console.error('Get all stores for chat error:', error);
    // Return known store to ensure chat always works
    return {
      success: true,
      data: [defaultStore]
    };
  }
};

// Get chat list by user ID
export const getChatListByUserId = async (userId, token) => {
  try {
    const authToken = token || Cookies.get('authToken');
    if (!authToken) {
      return { success: false, message: 'No auth token found', data: [] };
    }

    // Ensure userId is a number like in Angular app
    const numericUserId = parseInt(userId);
    if (isNaN(numericUserId)) {
      return { success: false, message: 'Invalid user ID', data: [] };
    }

    const formBody = toFormData({ id: numericUserId });

    const response = await axios.post(`${BASE_URL}v1/chats/getChatListBUid`, formBody, {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        'Authorization': `Bearer ${authToken}`
      },
      timeout: 10000  
    });

    const data = response.data;


    if (data && data.status && data.status === 200 && data.data) {
      // Return all chats (both store and admin)

      
      return { success: true, data: data.data };
    } else {
      return {
        success: false,
        message: data.message || 'Failed to fetch chat list',
        data: []
      };
    }
  } catch (error) {
    console.error('Get chat list API error:', error);

    // Handle specific error codes like Angular app
    if (error.response?.status === 401) {
      return {
        success: false,
        message: 'Unauthorized Request! Please login again.',
        data: []
      };
    } else if (error.response?.status === 500) {
      return {
        success: false,
        message: 'Something went wrong. Please try again.',
        data: []
      };
    } else if (error.response?.status === 422) {
      return {
        success: false,
        message: error.response.data?.error || 'Validation error',
        data: []
      };
    } else {
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Network error. Please try again.',
        data: []
      };
    }
  }
};

// Get chat rooms between users
export const getChatRooms = async (userId, participantId, token, receiverType = 'store') => {
  try {
    const authToken = token || Cookies.get('authToken');
    if (!authToken) {
      return { success: false, message: 'No auth token found' };
    }

    // Ensure userId is numeric, allow participantId to be string for store chat
    const numericUserId = parseInt(userId);
    let processedParticipantId = participantId;

    // If participantId is not a number (like store IDs), keep it as string
    // Otherwise convert to number
    if (!isNaN(parseInt(participantId))) {
      processedParticipantId = parseInt(participantId);
    }

    if (isNaN(numericUserId)) {
      return { success: false, message: 'Invalid user ID' };
    }

    const formBody = toFormData({
      uid: numericUserId,
      participants: processedParticipantId
      // Removed receiver_type as it's not used in Pure Meat Angular app
    });

    const response = await axios.post(`${BASE_URL}v1/chats/getChatRooms`, formBody, {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        'Authorization': `Bearer ${authToken}`
      },
      timeout: 10000
    });

    const data = response.data;


    if (data && data.status && data.status === 200) {
      return { success: true, data: data.data, data2: data.data2 };
    } else {
      return {
        success: false,
        message: data.message || 'Failed to get chat rooms'
      };
    }
  } catch (error) {
    console.error('Get chat rooms API error:', error);

    // Handle specific error codes like Angular app
    if (error.response?.status === 401) {
      return {
        success: false,
        message: 'Unauthorized Request! Please login again.'
      };
    } else if (error.response?.status === 500) {
      return {
        success: false,
        message: 'Something went wrong. Please try again.'
      };
    } else if (error.response?.status === 422) {
      return {
        success: false,
        message: error.response.data?.error || 'Validation error'
      };
    } else {
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Network error. Please try again.'
      };
    }
  }
};

// Create new chat rooms
export const createChatRooms = async (userId, participantId, token, receiverType = 'store') => {
  try {
    const authToken = token || Cookies.get('authToken');
    if (!authToken) {
      return { success: false, message: 'No auth token found' };
    }

    // Ensure userId is numeric, allow participantId to be string for store chat
    const numericUserId = parseInt(userId);
    let processedParticipantId = participantId;

    // If participantId is not a number (like store IDs), keep it as string
    // Otherwise convert to number
    if (!isNaN(parseInt(participantId))) {
      processedParticipantId = parseInt(participantId);
    }

    if (isNaN(numericUserId)) {
      return { success: false, message: 'Invalid user ID' };
    }

    const formBody = toFormData({
      uid: numericUserId,
      participants: processedParticipantId,
      status: 1
      // Removed receiver_type as it's not used in Pure Meat Angular app
    });

    const response = await axios.post(`${BASE_URL}v1/chats/createChatRooms`, formBody, {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        'Authorization': `Bearer ${authToken}`
      },
      timeout: 10000
    });

    const data = response.data;


    if (data && data.status && data.status === 200 && data.data) {
      return { success: true, data: data.data };
    } else {
      return {
        success: false,
        message: data.message || 'Failed to create chat room'
      };
    }
  } catch (error) {
    console.error('Create chat rooms API error:', error);

    // Handle specific error codes like Angular app
    if (error.response?.status === 401) {
      return {
        success: false,
        message: 'Unauthorized Request! Please login again.'
      };
    } else if (error.response?.status === 500) {
      return {
        success: false,
        message: 'Something went wrong. Please try again.'
      };
    } else if (error.response?.status === 422) {
      return {
        success: false,
        message: error.response.data?.error || 'Validation error'
      };
    } else {
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Network error. Please try again.'
      };
    }
  }
};

// Get chat messages by room ID
export const getChatMessagesByRoomId = async (roomId, token) => {
  try {
    const authToken = token || Cookies.get('authToken');
    if (!authToken) {
      return { success: false, message: 'No auth token found', data: [] };
    }

    // Ensure roomId is numeric
    const numericRoomId = parseInt(roomId);
    if (isNaN(numericRoomId)) {
      return { success: false, message: 'Invalid room ID', data: [] };
    }

    const formBody = toFormData({ room_id: numericRoomId });

    const response = await axios.post(`${BASE_URL}v1/chats/getById`, formBody, {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        'Authorization': `Bearer ${authToken}`
      },
      timeout: 10000
    });

    const data = response.data;


    if (data && data.status && data.status === 200 && data.data) {
      return { success: true, data: data.data };
    } else {
      return {
        success: false,
        message: data.message || 'Failed to fetch chat messages',
        data: []
      };
    }
  } catch (error) {
    console.error('Get chat messages API error:', error);

    // Handle specific error codes like Angular app
    if (error.response?.status === 401) {
      return {
        success: false,
        message: 'Unauthorized Request! Please login again.',
        data: []
      };
    } else if (error.response?.status === 500) {
      return {
        success: false,
        message: 'Something went wrong. Please try again.',
        data: []
      };
    } else if (error.response?.status === 422) {
      return {
        success: false,
        message: error.response.data?.error || 'Validation error',
        data: []
      };
    } else {
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Network error. Please try again.',
        data: []
      };
    }
  }
};

// Send message
export const sendChatMessage = async (messageData, token) => {
  try {
    const authToken = token || Cookies.get('authToken');
    if (!authToken) {
      return { success: false, message: 'No auth token found' };
    }

    // Ensure numeric values like in Angular app
    const sanitizedData = {
      room_id: parseInt(messageData.room_id),
      uid: parseInt(messageData.uid),
      from_id: parseInt(messageData.from_id),
      message: messageData.message,
      message_type: parseInt(messageData.message_type) || 0,
      status: parseInt(messageData.status) || 1
      // Removed receiver_type as it's not used in Pure Meat Angular app
    };

    // Validate required numeric fields
    if (isNaN(sanitizedData.room_id) || isNaN(sanitizedData.uid) || isNaN(sanitizedData.from_id)) {
      return { success: false, message: 'Invalid message data - missing required numeric fields' };
    }

    const formBody = toFormData(sanitizedData);

    const response = await axios.post(`${BASE_URL}v1/chats/sendMessage`, formBody, {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        'Authorization': `Bearer ${authToken}`
      },
      timeout: 15000 // Longer timeout for sending messages
    });

    const data = response.data;


    if (data && data.status && data.status === 200) {
      return { success: true, data: data.data };
    } else {
      return {
        success: false,
        message: data.message || 'Failed to send message'
      };
    }
  } catch (error) {
    console.error('Send chat message API error:', error);

    // Handle specific error codes like Angular app
    if (error.response?.status === 401) {
      return {
        success: false,
        message: 'Unauthorized Request! Please login again.'
      };
    } else if (error.response?.status === 500) {
      return {
        success: false,
        message: 'Something went wrong. Please try again.'
      };
    } else if (error.response?.status === 422) {
      return {
        success: false,
        message: error.response.data?.error || 'Validation error'
      };
    } else {
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Network error. Please try again.'
      };
    }
  }
};

// Get admin/support information
export const getAdminInfo = async () => {
  try {
    const response = await axios.get(`${BASE_URL}v1/settings/getDefaultWeb`, {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded'
      },
      timeout: 10000
    });

    const data = response.data;


    if (data && data.status && data.status === 200 && data.data && data.data.support) {
      return { success: true, data: data.data.support };
    } else {
      return {
        success: false,
        message: 'Failed to get admin info'
      };
    }
  } catch (error) {
    console.error('Get admin info API error:', error);
    return {
      success: false,
      message: error.response?.data?.message || error.message || 'Network error. Please try again.'
    };
  }
};